import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { X, Bot, User as UserIcon, Webhook, Filter } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useUI } from '@/lib/store';
import { useAuditEntries } from '@/api/audit';
import { relativeTime } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';

type ActorKind = 'staff' | 'system' | 'webhook';

interface ActivityItem {
  id: string;
  timestamp: string;
  actor: string;
  actorKind: ActorKind;
  category: 'staff' | 'system';
  title: string;
  detail?: string;
  tenantSlug?: string;
  tenantName?: string;
}

export function ActivityPanel() {
  const open = useUI(s => s.activityOpen);
  const setOpen = useUI(s => s.setActivityOpen);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === 'A' && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  const { data: apiAudit } = useAuditEntries();
  const feed = useMemo<ActivityItem[]>(() => {
    return (apiAudit ?? []).map(e => ({
      id: e.id,
      timestamp: e.timestamp,
      actor: e.staff_name,
      actorKind: e.staff_name === 'system' || e.staff_email === '—' ? 'system' : 'staff',
      category: e.staff_name === 'system' ? 'system' : 'staff',
      title: e.action,
      detail: e.target,
      tenantSlug: e.tenant_slug ?? e.tenant_id ?? undefined,
      tenantName: e.tenant_name ?? undefined,
    }));
  }, [apiAudit]);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[80] bg-black/20 animate-fade-in"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        className={cn(
          'fixed top-0 right-0 h-full w-[380px] bg-bg-elevated border-l border-border z-[90] transition-transform duration-200 ease-standard flex flex-col',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="h-12 px-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-md font-semibold text-fg">Activity</h2>
            <span className="text-2xs text-fg-subtle">{feed.length} events</span>
          </div>
          <div className="flex items-center gap-1">
            <button className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-2" aria-label="Filter">
              <Filter size={13} />
            </button>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 inline-flex items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-2"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {feed.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-fg-muted">
              No activity yet. Operator actions and system events will appear here.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {feed.map(item => (
                <li key={item.id} className="px-4 py-3 hover:bg-surface-2/50 transition-colors">
                  <div className="flex items-start gap-2.5">
                    <ActorIcon kind={item.actorKind} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-medium text-fg">{item.actor}</span>
                        <ActorBadge kind={item.actorKind} />
                        <span className="ml-auto text-2xs text-fg-subtle whitespace-nowrap">
                          {relativeTime(item.timestamp)}
                        </span>
                      </div>
                      <div className="mt-0.5 text-sm text-fg-muted">
                        <span className="font-mono text-xs text-fg">{item.title}</span>
                        {item.detail && <> · {item.detail}</>}
                      </div>
                      {item.tenantSlug && (
                        <Link
                          to={`/tenants/${item.tenantSlug}`}
                          onClick={() => setOpen(false)}
                          className="mt-1 inline-block text-2xs text-accent hover:underline"
                        >
                          {item.tenantName ?? item.tenantSlug} →
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="px-4 py-2 border-t border-border text-2xs text-fg-subtle flex items-center justify-between shrink-0">
          <span>{feed.length} events</span>
          <Link to="/audit" className="text-accent hover:underline" onClick={() => setOpen(false)}>
            Full audit log →
          </Link>
        </footer>
      </aside>
    </>
  );
}

function ActorIcon({ kind }: { kind: ActorKind }) {
  if (kind === 'staff') {
    return (
      <span className="w-6 h-6 rounded-full bg-surface-2 border border-border flex items-center justify-center text-fg-muted shrink-0 mt-0.5">
        <UserIcon size={11} />
      </span>
    );
  }
  if (kind === 'system') {
    return (
      <span className="w-6 h-6 rounded-full bg-[color:var(--accent-soft)] border border-transparent flex items-center justify-center text-accent shrink-0 mt-0.5">
        <Bot size={11} />
      </span>
    );
  }
  return (
    <span className="w-6 h-6 rounded-full bg-[color:var(--warn-soft)] border border-transparent flex items-center justify-center text-[color:var(--warn)] shrink-0 mt-0.5">
      <Webhook size={11} />
    </span>
  );
}

function ActorBadge({ kind }: { kind: ActorKind }) {
  if (kind === 'staff') return <Badge tone="neutral">Staff</Badge>;
  if (kind === 'system') return <Badge tone="accent">System</Badge>;
  return <Badge tone="warn">Webhook</Badge>;
}

function isTypingTarget(t: EventTarget | null) {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  const tag = t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
